import { AddPlusButton } from '@components/core-ui/buttons/AddPlusButton';
import { Button } from '@components/core-ui/form-elements/buttons/Button';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { AdminJobTopActionButton } from '@components/core-ui/form-elements/buttons/Button/variations/AdminJobTopActionButton';
import { FileIconLinkButton } from '@components/core-ui/form-elements/buttons/Button/variations/FileIconLinkButton';
import {
  LeftTabButton,
  RightTabButton,
} from '@components/core-ui/form-elements/buttons/Button/variations/JobHistoryTabButtons';
import { NeutralButtonChrome } from '@components/core-ui/form-elements/buttons/Button/variations/NeutralButtonChrome';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ButtonMedium } from '@components/core-ui/form-elements/buttons/Button/variations/PrimaryButtonSizes';
import { SwitchButton } from '@components/core-ui/form-elements/buttons/Button/variations/SwitchButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { TextButtonLarge } from '@components/core-ui/form-elements/buttons/Button/variations/TextButtonLarge';
import { ToolbarActionButton } from '@components/core-ui/form-elements/buttons/Button/variations/ToolbarActionButton';
import { InlineInheritIconButton } from '@components/core-ui/form-elements/buttons/InlineInheritIconButton';
import { IconButtonClean } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonClean';
import { IconButtonDelete } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonDelete';
import { IconButtonRegular } from '@components/core-ui/form-elements/buttons/IconButton/variations/IconButtonRegular';
import FileIcon from '@components/icons/File.svg';
import CrossIcon from '@components/icons/Cross.svg';
import EditIcon from '@components/icons/Edit.svg';
import TrashBin from '@components/icons/TrashBin.svg';
import ClosedIcon from '@components/icons/ClosedChevron.svg';
import ModalCloseIcon from '@components/icons/ModalClose.svg';
import { ReactNode } from 'react';

const noop = () => undefined;

const thClassName =
  'border-b border-[var(--color-border)] px-3 py-2 text-left text-xs font-semibold text-[var(--color-text-muted)]';
const tdClassName = 'border-b border-[var(--color-border)] px-3 py-3 align-middle';
const nameCellClassName = 'min-w-[200px] text-sm font-medium text-[var(--color-text)]';
const groupHeadingClassName =
  'bg-[var(--color-surface-muted)] text-xs font-semibold uppercase tracking-wide text-[var(--color-text-muted)]';

const ButtonStates = ({
  cellClassName,
  children,
}: {
  cellClassName?: string;
  children: (disabled: boolean) => ReactNode;
}) => {
  const wrapClassName = ['flex min-h-[40px] items-center', cellClassName, cellClassName ? 'rounded px-2 py-1' : '']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <td className={tdClassName}>
        <div className={wrapClassName}>{children(false)}</div>
      </td>
      <td className={tdClassName}>
        <div className={wrapClassName}>{children(true)}</div>
      </td>
    </>
  );
};

const ButtonRow = ({
  name,
  hint,
  cellClassName,
  children,
}: {
  name: string;
  hint?: string;
  cellClassName?: string;
  children: (disabled: boolean) => ReactNode;
}) => (
  <tr>
    <td className={`${tdClassName} ${nameCellClassName}`}>
      {name}
      {hint ? <span className="mt-0.5 block text-xs font-normal text-foreground-muted">{hint}</span> : null}
    </td>
    <ButtonStates cellClassName={cellClassName}>{children}</ButtonStates>
  </tr>
);

const GroupHeading = ({ title }: { title: string }) => (
  <tr>
    <td colSpan={3} className={`${tdClassName} ${groupHeadingClassName}`}>
      {title}
    </td>
  </tr>
);

export const ButtonsDemo = () => (
  <div className="overflow-x-auto">
    <p className="mb-3 text-xs text-foreground-muted">
      Hover each row to check hover styles. Disabled uses the native disabled attribute.
    </p>
    <table className="w-full min-w-[520px] border-collapse text-left">
      <thead>
        <tr>
          <th className={thClassName}>Variation</th>
          <th className={thClassName}>Default</th>
          <th className={thClassName}>Disabled</th>
        </tr>
      </thead>
      <tbody>
        <GroupHeading title="Primary" />
        <ButtonRow name="Button">
          {(disabled) => (
            <Button disabled={disabled} onClick={noop}>
              Label
            </Button>
          )}
        </ButtonRow>
        <ButtonRow name="ButtonMedium">
          {(disabled) => (
            <ButtonMedium disabled={disabled} onClick={noop}>
              Label
            </ButtonMedium>
          )}
        </ButtonRow>
        <ButtonRow name="BoldButton">
          {(disabled) => (
            <BoldButton disabled={disabled} onClick={noop}>
              Label
            </BoldButton>
          )}
        </ButtonRow>

        <GroupHeading title="Outlined" />
        <ButtonRow name="OutlinedButton">
          {(disabled) => (
            <OutlinedButton disabled={disabled} onClick={noop}>
              Label
            </OutlinedButton>
          )}
        </ButtonRow>
        <GroupHeading title="Text & neutral" />
        <ButtonRow name="TextButton">
          {(disabled) => (
            <TextButton disabled={disabled} onClick={noop}>
              Label
            </TextButton>
          )}
        </ButtonRow>
        <ButtonRow name="TextButtonLarge">
          {(disabled) => (
            <TextButtonLarge disabled={disabled} onClick={noop}>
              Label
            </TextButtonLarge>
          )}
        </ButtonRow>
        <ButtonRow name="NeutralButtonChrome">
          {(disabled) => (
            <NeutralButtonChrome disabled={disabled} onClick={noop}>
              Label
            </NeutralButtonChrome>
          )}
        </ButtonRow>

        <GroupHeading title="Icon" />
        <ButtonRow name="IconButtonRegular" hint="e.g. edit, table row actions">
          {(disabled) => (
            <IconButtonRegular disabled={disabled} onClick={noop} title="Edit">
              <EditIcon />
            </IconButtonRegular>
          )}
        </ButtonRow>
        <ButtonRow name="IconButtonDelete">
          {(disabled) => (
            <IconButtonDelete disabled={disabled} onClick={noop} title="Delete">
              <TrashBin />
            </IconButtonDelete>
          )}
        </ButtonRow>
        <ButtonRow name="IconButtonClean" hint="e.g. modal close, tables, inputs">
          {(disabled) => (
            <IconButtonClean disabled={disabled} onClick={noop} title="Close">
              <ModalCloseIcon />
            </IconButtonClean>
          )}
        </ButtonRow>
        <ButtonRow name="InlineInheritIconButton" hint="Inherits parent text color">
          {(disabled) => (
            <span className="text-sm text-[var(--color-accent)]">
              Label{' '}
              <InlineInheritIconButton disabled={disabled} onClick={noop} title="Inline action">
                <ClosedIcon />
              </InlineInheritIconButton>
            </span>
          )}
        </ButtonRow>

        <GroupHeading title="Toggle & tabs" />
        <ButtonRow name="SwitchButton" hint="Inactive / active">
          {(disabled) => (
            <div className="inline-flex">
              <SwitchButton active={false} disabled={disabled} onClick={noop}>
                Off
              </SwitchButton>
              <SwitchButton active disabled={disabled} onClick={noop}>
                On
              </SwitchButton>
            </div>
          )}
        </ButtonRow>
        <ButtonRow name="LeftTabButton active" hint="Left active / right inactive">
          {(disabled) => (
            <div className="inline-flex">
              <LeftTabButton $active disabled={disabled} onClick={noop}>
                Tab
              </LeftTabButton>
              <RightTabButton $active={false} disabled={disabled} onClick={noop}>
                Tab
              </RightTabButton>
            </div>
          )}
        </ButtonRow>
        <ButtonRow name="RightTabButton active" hint="Left inactive / right active">
          {(disabled) => (
            <div className="inline-flex">
              <LeftTabButton $active={false} disabled={disabled} onClick={noop}>
                Tab
              </LeftTabButton>
              <RightTabButton $active disabled={disabled} onClick={noop}>
                Tab
              </RightTabButton>
            </div>
          )}
        </ButtonRow>
        <ButtonRow name="Tab buttons" hint="Both inactive">
          {(disabled) => (
            <div className="inline-flex">
              <LeftTabButton $active={false} disabled={disabled} onClick={noop}>
                Tab
              </LeftTabButton>
              <RightTabButton $active={false} disabled={disabled} onClick={noop}>
                Tab
              </RightTabButton>
            </div>
          )}
        </ButtonRow>

        <GroupHeading title="Context-specific" />
        <ButtonRow name="ToolbarActionButton">
          {(disabled) => (
            <ToolbarActionButton disabled={disabled} onClick={noop} title="Toolbar action">
              <CrossIcon className="h-[18px] w-[18px]" />
              Action
            </ToolbarActionButton>
          )}
        </ButtonRow>
        <ButtonRow name="AdminJobTopActionButton">
          {(disabled) => (
            <AdminJobTopActionButton disabled={disabled} onClick={noop} title="Admin action">
              Action
            </AdminJobTopActionButton>
          )}
        </ButtonRow>
        <ButtonRow name="FileIconLinkButton">
          {(disabled) => (
            <FileIconLinkButton disabled={disabled} onClick={noop} title="File">
              <FileIcon />
              file.pdf
            </FileIconLinkButton>
          )}
        </ButtonRow>
        <GroupHeading title="Add card" />
        <ButtonRow name="AddPlusButton">
          {(disabled) => (
            <ul className="m-0 list-none p-0">
              <AddPlusButton onClick={noop} disabled={disabled} />
            </ul>
          )}
        </ButtonRow>
        <ButtonRow name="AddPlusButton" hint="variant workspace">
          {(disabled) => (
            <ul className="m-0 list-none p-0">
              <AddPlusButton onClick={noop} variant="workspace" disabled={disabled} />
            </ul>
          )}
        </ButtonRow>
        <ButtonRow name="AddPlusButton" hint="variant avatar">
          {(disabled) => (
            <ul className="m-0 list-none p-0">
              <AddPlusButton onClick={noop} variant="avatar" disabled={disabled} />
            </ul>
          )}
        </ButtonRow>
      </tbody>
    </table>
  </div>
);
